import { useEffect, useState } from "react";
import {
  useAlertList,
  useNoticeReceiverList,
  useNoticeSenderList,
  useNoticeSendSubData,
} from "~/services/customer/notice";
import type { InformListSnapshot } from "&/informList";
// import { format } from "date-fns";

interface NoticeListType {
  date: string;
  notices: NoticeType[];
}

export interface NoticeType {
  type: string;
  message: string;
  readYn?: "Y" | "N";
  notiRecvSeq?: number;
  reservationSeq?: number;
  cdReservStatus?: string;
  cdReservType?: string;
  companyName?: string;
}

export interface NoticeContentType {
  cdNotiType: string;
  /** 실제 API 응답 필드 (FindNoticePageNationResponseDto.dtSend) */
  dtSend?: string;
  dtRegist: string;
  notiContents: string;
  notiTitle: string;
  notiRecvSeq: number;
  petSeq: number;
  readYn: "Y" | "N";
  iconType: "calendar" | "notify" | "gallery" | "notice";
  fileUrls?: string[]; // 이미지 URL 배열 추가
  files?: Array<{
    fileSeq?: number;
    fileUrl?: string;
    thumbnailUrl?: string | null;
    thumbnailStatus?: string | null;
  }>;
  cdReservStatus?: string;
  cdReservType?: string;
  companyName?: string;
  reservationSeq?: number;
  userNewReplyYn: "Y" | "N";
}

// NOTI_TYPE_001 : 필수 알림(관리자 공지)
// NOTI_TYPE_002 : 마케팅 알림(관리자 공지)
// NOTI_TYPE_003 : 알림장
// NOTI_TYPE_004 : 캘린더
// NOTI_TYPE_005 : 공지사항
// NOTI_TYPE_006 : 사진첩
// NOTI_TYPE_007 : 예약정보 변경
// NOTI_TYPE_008 : 예약정보 확정
// NOTI_TYPE_009 : 예약정보 취소

// 알림 타입에 따른 아이콘 매핑
const getIconType = (noticeType: string) => {
  switch (noticeType) {
    case "NOTI_TYPE_003":
      return "notify"; // 알림장
    case "NOTI_TYPE_004":
      return "calendar"; // 캘린더
    case "NOTI_TYPE_005":
      return "notice"; // 공지사항
    case "NOTI_TYPE_006":
      return "gallery"; // 사진첩
    case "NOTI_TYPE_007":
      return "calendar"; // 예약정보 변경
    case "NOTI_TYPE_008":
      return "calendar"; // 예약정보 확정
    case "NOTI_TYPE_009":
      return "calendar"; // 예약정보 취소
    default:
      return "notice";
  }
};

const useNotice = (initial?: InformListSnapshot) => {
  const { data: alertData, refetch: refetchAlertList } = useAlertList();
  const [pageSender, setPageSender] = useState(initial?.pageSender ?? 1);
  const [pageReceiver, setPageReceiver] = useState(initial?.pageReceiver ?? 1);
  const [isSenderLast, setIsSenderLast] = useState(true);
  const [isReceiverLast, setIsReceiverLast] = useState(true);
  // 복원 부트스트랩: 저장된 페이지 수만큼(page*10)을 1페이지 한 번에 재조회 후 목록 교체.
  // 이후 무한 스크롤로 다음 페이지를 붙이는 순간 일반 페이징(size 10)으로 전환
  const [bootstrapSender, setBootstrapSender] = useState(!!initial);
  const [bootstrapReceiver, setBootstrapReceiver] = useState(!!initial);
  const [noticeSenderList, setNoticeSenderList] = useState<NoticeContentType[]>(
    []
  );
  const [noticeReceiverList, setNoticeReceiverList] = useState<
    NoticeContentType[]
  >([]);
  const {
    data: noticeSenderData,
    isLoading: isSenderLoading,
    isFetching: isSenderFetching,
    refetch: refetchSenderList,
  } = useNoticeSenderList(
    bootstrapSender
      ? { cdSenderType: "SENDER_PET_OWNER", page: 1, size: pageSender * 10 }
      : { cdSenderType: "SENDER_PET_OWNER", page: pageSender, size: 10 }
  );
  const {
    data: noticeReceiverData,
    isLoading: isReceiverLoading,
    isFetching: isReceiverFetching,
    refetch: refetchReceiverList,
  } = useNoticeReceiverList(
    bootstrapReceiver
      ? {
          cdRecipientType: "RECIPIENT_PET_OWNER",
          page: 1,
          size: pageReceiver * 10,
        }
      : { cdRecipientType: "RECIPIENT_PET_OWNER", page: pageReceiver, size: 10 }
  );
  const { data: noticeSendSubData } = useNoticeSendSubData();
  console.log(noticeSendSubData);
  useEffect(() => {
    if (noticeSenderData && noticeSenderData.content.length > 0) {
      const result = noticeSenderData.content.map((item: NoticeContentType) => {
        return {
          ...item,
          iconType: getIconType(item.cdNotiType),
          userNewReplyYn: item.userNewReplyYn,
        };
      });
      if (pageSender === 1 || bootstrapSender) {
        // 첫 페이지 또는 복원 부트스트랩(누적분 일괄 조회) → 목록 교체
        setNoticeSenderList(result);
      } else {
        // 같은 페이지가 재조회될 수 있으므로 중복 제거 후 추가
        setNoticeSenderList((prev) => {
          const seen = new Set(prev.map((e) => e.notiRecvSeq));
          return [
            ...prev,
            ...result.filter(
              (e: NoticeContentType) => !seen.has(e.notiRecvSeq)
            ),
          ];
        });
      }
      setIsSenderLast(noticeSenderData.last);
    }
  }, [noticeSenderData, pageSender, bootstrapSender]);

  useEffect(() => {
    if (noticeReceiverData && noticeReceiverData.content.length > 0) {
      const result = noticeReceiverData.content.map(
        (item: NoticeContentType) => {
          return {
            ...item,
            iconType: getIconType(item.cdNotiType),
          };
        }
      );
      if (pageReceiver === 1 || bootstrapReceiver) {
        // 첫 페이지 또는 복원 부트스트랩(누적분 일괄 조회) → 목록 교체
        setNoticeReceiverList(result);
      } else {
        // 같은 페이지가 재조회될 수 있으므로 중복 제거 후 추가
        setNoticeReceiverList((prev) => {
          const seen = new Set(prev.map((e) => e.notiRecvSeq));
          return [
            ...prev,
            ...result.filter(
              (e: NoticeContentType) => !seen.has(e.notiRecvSeq)
            ),
          ];
        });
      }
      setIsReceiverLast(noticeReceiverData.last);
    }
  }, [noticeReceiverData, pageReceiver, bootstrapReceiver]);

  // ※ 언마운트 클린업으로 페이지/목록을 리셋하지 않는다.
  //    로컬 state는 언마운트 시 어차피 소멸하고, StrictMode(개발)의 마운트→클린업→재마운트
  //    시뮬레이션에서 복원된 페이지 수(pageReceiver/pageSender)가 1로 되돌아가
  //    뒤로가기 스크롤 복원이 깨지는 버그의 원인이었음.

  const alertList = alertData?.map((data: NoticeListType) => {
    return {
      date: data.date,
      notices: data.notices.map((item: NoticeType) => {
        return {
          type: item.type,
          message: item.message,
          readYn: item.readYn || "N", // readYn이 없으면 기본값 "N"
          notiRecvSeq: item.notiRecvSeq, // notiRecvSeq 포함
          iconType: getIconType(item.type),
          reservationSeq: item.reservationSeq,
          cdReservStatus: item.cdReservStatus,
          cdReservType: item.cdReservType,
          companyName: item.companyName,
        };
      }),
    };
  });

  const handlePage = (tab: string) => {
    if (tab === "받은 알림장") {
      // 부트스트랩(일괄 조회) 이후 다음 페이지부터는 일반 페이징으로 전환
      setBootstrapReceiver(false);
      setPageReceiver((prev) => prev + 1);
    } else {
      setBootstrapSender(false);
      setPageSender((prev) => prev + 1);
    }
  };

  return {
    alertList,
    handlePage,
    noticeSenderList,
    noticeReceiverList,
    pageSender,
    pageReceiver,
    isSenderLast,
    isReceiverLast,
    isLoading: isSenderLoading || isReceiverLoading,
    isFetching: isSenderFetching || isReceiverFetching,
    refetchAlertList,
    refetchSenderList,
    refetchReceiverList,
  };
};

export default useNotice;
